import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { API, axios } from '../../utils/api';
import { euro } from '../../utils/scheda';
import Icona from '../cliente/Icone';
import './provvigioni.css';

// La provvigione di una pratica (una proposta ACCETTATA): si apre dalla scheda cliente → Proposte e dalla scheda
// dell'agenzia. Dentro: il compenso dal cliente (importo fisso o percentuale sul prezzo, IVA sì/no), la provvigione
// dell'agenzia (percentuale o importo, concordata → fatturata → incassata con le date), la quota del collaboratore
// (di chi è e quanto: l'elenco delle persone lo manda il server in `persone`, di base chi segue il cliente),
// le spese, le note e lo stato della pratica (prevista → fatturata → incassata).
// Sono soldi: di tutto questo il cliente non vede niente: sta solo nelle pagine dello staff.
//
// I conti NON si rifanno qui: gli importi mostrati sono quelli di `conti` che manda il server (imponibile, IVA,
// lordo, netto, quota, margine). Nel modulo si scrive, e i conti tornano appena si salva.
//
// Chi vede cosa lo decide il server (backend/provvigioni.py): all'admin arriva tutto, al collaboratore SOLO la sua
// quota (`quota`, niente `conti`, niente agenzia) — e la pagina disegna solo i pezzi che arrivano.

// Le rotte del backend, tutte qui
export const ROTTE = {
  pratica: (propostaId) => `/provvigioni/pratica/${propostaId}`,
  stato: (propostaId) => `/provvigioni/pratica/${propostaId}/stato`,
  pratiche: '/provvigioni/pratiche',
  riepilogo: '/provvigioni/riepilogo',
  agenzie: '/provvigioni/agenzie',
  collaboratori: '/provvigioni/collaboratori',
  csv: '/provvigioni.csv',
  impostazioni: '/impostazioni/provvigioni',
};

// Stato della pratica e stato con l'agenzia: parola e colore del tag (staff.css)
export const STATI = { prevista: ['t-o', 'prevista'], fatturata: ['t-b', 'fatturata'], incassata: ['t-g', 'incassata'] };
export const STATI_AGENZIA = { concordata: ['t-o', 'concordata'], fatturata: ['t-b', 'fatturata'], incassata: ['t-g', 'incassata'] };
// I passi si fanno uno alla volta, come li accetta il server (POST …/stato)
export const DOPO = { prevista: 'fatturata', fatturata: 'incassata', incassata: null };
export const DOPO_AGENZIA = { concordata: 'fatturata', fatturata: 'incassata', incassata: null };

export const tagStato = (stato, tabella = STATI) => tabella[stato] || ['t-b', stato || '—'];

// «2,5 %» (il server manda 2.5); niente percentuale inventata quando non c'è
export const percento = (valore) => {
  if (valore === null || valore === undefined || valore === '') return null;
  const n = Number(valore);
  if (Number.isNaN(n)) return null;
  return `${n.toLocaleString('it-IT', { maximumFractionDigits: 2 })} %`;
};

// «12 set 2026»
export const giorno = (valore) => {
  if (!valore) return null;
  const d = new Date(valore);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('it-IT', { day: 'numeric', month: 'short', year: 'numeric' });
};

// 0 € è un importo vero (euro() da solo torna null): nei conti lo zero si scrive
export const soldi = (valore) => (valore === null || valore === undefined || valore === '' ? null : euro(valore) || '0 €');

const perInput = (v) => (v === null || v === undefined ? '' : String(v).slice(0, 10));
const numero = (v) => (v === null || v === undefined ? '' : String(v));

// Perché la provvigione non si vede: quasi sempre lo dice il server (detail), il resto sono i casi nostri
export const GENERICI = {
  400: 'Questa proposta non è accettata: non è ancora una pratica.',
  403: 'Questa provvigione non si può vedere: il collaboratore vede solo la sua quota.',
  404: 'Non trovo questa pratica: o la proposta non c\'è più, o le provvigioni non sono ancora collegate al server.',
  vuoto: 'Il server ha risposto senza la provvigione. La pagina è pronta e la mostra appena arriva.',
  errore: 'Non riesco a caricare la provvigione. Riprovate tra poco.',
};
export const perche = (e, tabella = GENERICI) => {
  const stato = e?.response?.status;
  const detail = e?.response?.data?.detail;
  // Un 400/403/404 porta un messaggio scritto per chi legge, e si mostra così com'è. Un errore del server (5xx)
  // no: quello che dice è roba sua ("boom", un'eccezione) e a video finiva tale e quale. Lì vale il nostro
  // messaggio, come in tutte le altre pagine (blocco 3, secondo giro).
  if (typeof detail === 'string' && detail.trim() && !(stato >= 500)) return detail;
  return tabella[stato] || tabella.errore;
};

// La risposta è una pratica? (in locale il sito e le rotte stanno sullo stesso indirizzo: una rotta che non c'è
// torna la pagina del sito, e senza questo controllo la pagina si riempirebbe di trattini)
const CHIAVI = ['proposta_id', 'c_e', 'quota', 'conti', 'provvigione'];
export const sembraPratica = (d) => Boolean(d) && typeof d === 'object' && !Array.isArray(d) && CHIAVI.some((k) => k in d);

// Lo stato in cui sta adesso la pratica (e quello con l'agenzia): quello salvato, altrimenti quello della bozza
export const statoDi = (dati) => dati?.provvigione?.stato || dati?.bozza?.stato || 'prevista';
export const statoAgenziaDi = (dati) => dati?.provvigione?.agenzia?.stato || dati?.bozza?.agenzia?.stato || 'concordata';

// Il modulo parte dalla provvigione salvata (`provvigione`); quando non c'è ancora, dalla `bozza` che il server
// prepara coi valori di Impostazioni → Provvigioni (senza, le caselle partirebbero vuote e «compila e salva»
// scriverebbe una pratica a zero); in mancanza di tutto, almeno dal prezzo della pratica.
export const moduloDa = (dati = {}) => {
  const p = dati.provvigione || dati.bozza || {};
  const c = p.compenso || {};
  const a = p.agenzia || {};
  const q = p.quota || {};
  return {
    prezzo: numero(p.prezzo ?? dati.prezzo_offerto ?? dati.prezzo_immobile),
    compenso_tipo: c.tipo || 'fisso',
    compenso_importo: numero(c.importo),
    compenso_percentuale: numero(c.percentuale),
    compenso_iva: c.iva !== false,
    agenzia_tipo: a.tipo || 'percentuale',
    agenzia_percentuale: numero(a.percentuale),
    agenzia_importo: numero(a.importo),
    agenzia_stato: a.stato || 'concordata',
    agenzia_data_concordata: perInput(a.data_concordata),
    agenzia_data_fatturata: perInput(a.data_fatturata),
    agenzia_data_incassata: perInput(a.data_incassata),
    // di chi è la quota: quella scritta sulla pratica, altrimenti chi segue il cliente (`persona_id` della pratica)
    quota_persona: q.persona_id || dati.persona_id || '',
    quota_percentuale: numero(q.percentuale),
    spese: (p.spese || []).map((s) => ({ descrizione: s.descrizione || '', importo: numero(s.importo) })),
    stato: p.stato || 'prevista',
    data_prevista: perInput(p.data_prevista ?? dati.data),
    data_fatturata: perInput(p.data_fatturata),
    data_incassata: perInput(p.data_incassata),
    importo_fatturato: numero(p.importo_fatturato),
    importo_incassato: numero(p.importo_incassato),
    note: p.note || '',
  };
};

// Gli stati (della pratica e con l'agenzia) vanno avanti UN passo alla volta, e ogni passo avanti vuole la sua
// data: sono le regole del server (POST …/stato). Il modulo le controlla prima di salvare — così non si arriva
// a «incassata» saltando «fatturata», o senza la data d'incasso.
const ORDINE = Object.keys(STATI);
const ORDINE_AGENZIA = Object.keys(STATI_AGENZIA);
const CASELLA_DATA = { fatturata: 'data_fatturata', incassata: 'data_incassata' };
const CASELLA_DATA_AGENZIA = { fatturata: 'agenzia_data_fatturata', incassata: 'agenzia_data_incassata' };
const NOME_CASELLA = { data_fatturata: 'Fatturata il', data_incassata: 'Incassata il',
  agenzia_data_fatturata: 'Fatturata il (agenzia)', agenzia_data_incassata: 'Incassata il (agenzia)' };

const passoStorto = (modulo, ordine, ora, scelto, caselle) => {
  const prima = ordine.indexOf(ora);
  const dopo = ordine.indexOf(scelto);
  if (prima < 0 || dopo < 0 || dopo <= prima) return null;     // fermo, o indietro: si può sempre (si corregge)
  if (dopo > prima + 1) return `Prima «${ordine[prima + 1]}»: gli stati si fanno uno alla volta.`;
  const casella = caselle[scelto];
  if (casella && !modulo[casella]) return `Per segnare «${scelto}» ci vuole la data: scrivetela in «${NOME_CASELLA[casella]}».`;
  return null;
};

// Perché questo modulo non si può salvare così com'è (null = si può)
export const controllaStati = (modulo, statoOra, statoAgenziaOra) => (
  passoStorto(modulo, ORDINE, statoOra, modulo.stato, CASELLA_DATA)
  || passoStorto(modulo, ORDINE_AGENZIA, statoAgenziaOra, modulo.agenzia_stato, CASELLA_DATA_AGENZIA));

// Il corpo del PUT, con i nomi del server. Le caselle vuote sono null (non stringhe vuote): il server le legge
// come «non c'è», e una spesa senza descrizione o senza importo non si manda proprio (la rifiuterebbe).
// Gli stati NON stanno qui: passano dalla rotta apposta (POST …/stato), che li fa uno alla volta e con la data.
export const corpoDaModulo = (m) => ({
  prezzo: m.prezzo || null,
  compenso: {
    tipo: m.compenso_tipo,
    importo: m.compenso_tipo === 'fisso' ? m.compenso_importo || null : null,
    percentuale: m.compenso_tipo === 'percentuale' ? m.compenso_percentuale || null : null,
    iva: m.compenso_iva,
  },
  agenzia: {
    tipo: m.agenzia_tipo,
    percentuale: m.agenzia_tipo === 'percentuale' ? m.agenzia_percentuale || null : null,
    importo: m.agenzia_tipo === 'importo' ? m.agenzia_importo || null : null,
    data_concordata: m.agenzia_data_concordata || null,
    data_fatturata: m.agenzia_data_fatturata || null,
    data_incassata: m.agenzia_data_incassata || null,
  },
  quota: { persona_id: m.quota_persona || null, percentuale: m.quota_percentuale || null },
  spese: (m.spese || []).filter((s) => s.descrizione.trim() && s.importo !== '')
    .map((s) => ({ descrizione: s.descrizione.trim(), importo: s.importo })),
  data_prevista: m.data_prevista || null,
  data_fatturata: m.data_fatturata || null,
  data_incassata: m.data_incassata || null,
  importo_fatturato: m.importo_fatturato || null,
  importo_incassato: m.importo_incassato || null,
  note: m.note || '',
});

// Cambiando la persona cambia anche la percentuale: ogni collaboratore ha la sua (nel profilo), e il server la
// manda dentro `persone`. Chi non ha una percentuale sua (o una persona che non è nell'elenco) lascia quella scritta.
export const cambiaPersonaQuota = (modulo, persone, personaId) => {
  const p = (persone || []).find((x) => x.id === personaId);
  const sua = p && p.percentuale !== null && p.percentuale !== undefined ? String(p.percentuale) : null;
  return { ...modulo, quota_persona: personaId, quota_percentuale: sua === null ? modulo.quota_percentuale : sua };
};

// Il nome di chi prende la quota, per scriverlo accanto all'importo (senza l'elenco, niente nome)
export const nomePersona = (persone, personaId) => (persone || []).find((p) => p.id === personaId)?.nome || null;

// Una voce dei conti: etichetta sopra, importo sotto (mai un importo inventato: se non arriva, «—»)
const Voce = ({ nome, valore, classe, testid }) => (
  <div data-testid={testid}>
    <small>{nome}</small>
    <b className={valore ? classe : 'vuoto'}>{valore || '—'}</b>
  </div>
);

// Il modulo: prezzo, compenso, agenzia, quota, spese, stato e note
const Modulo = ({ modulo, setModulo, salva, annulla, inVolo, nuova = false, persone = [] }) => {
  const cambia = (k) => (e) => setModulo({ ...modulo, [k]: e.target.value });
  const testo = (k, props = {}) => <input value={modulo[k]} onChange={cambia(k)} {...props} />;
  const data = (k) => <input type="date" value={modulo[k]} onChange={cambia(k)} />;
  const spesa = (i, k) => (e) => setModulo({ ...modulo, spese: modulo.spese.map((s, j) => (j === i ? { ...s, [k]: e.target.value } : s)) });
  return (
    <form className="prov-modulo" data-testid="modulo-provvigione" onSubmit={(e) => { e.preventDefault(); salva(); }}>
      <fieldset>
        <legend>Compenso dal cliente</legend>
        <div className="imp-campi">
          <label>Prezzo di vendita (€){testo('prezzo', { inputMode: 'decimal', placeholder: 'es. 250000' })}</label>
          <label>Come
            <select value={modulo.compenso_tipo} onChange={cambia('compenso_tipo')} aria-label="Tipo di compenso">
              <option value="fisso">Importo fisso</option>
              <option value="percentuale">Percentuale sul prezzo</option>
            </select>
          </label>
          {modulo.compenso_tipo === 'fisso'
            ? <label>Importo (€){testo('compenso_importo', { inputMode: 'decimal', placeholder: 'es. 3000' })}</label>
            : <label>Percentuale (%){testo('compenso_percentuale', { inputMode: 'decimal', placeholder: 'es. 3' })}</label>}
          <label>IVA
            <select value={modulo.compenso_iva ? 'si' : 'no'} onChange={(e) => setModulo({ ...modulo, compenso_iva: e.target.value === 'si' })} aria-label="IVA">
              <option value="si">Sì, con IVA</option>
              <option value="no">No, senza IVA</option>
            </select>
          </label>
        </div>
      </fieldset>
      <fieldset>
        <legend>Provvigione dell'agenzia</legend>
        <div className="imp-campi">
          <label>Come
            <select value={modulo.agenzia_tipo} onChange={cambia('agenzia_tipo')} aria-label="Tipo di provvigione dell'agenzia">
              <option value="percentuale">Percentuale sul prezzo</option>
              <option value="importo">Importo concordato</option>
            </select>
          </label>
          {modulo.agenzia_tipo === 'percentuale'
            ? <label>Percentuale dell'agenzia (%){testo('agenzia_percentuale', { inputMode: 'decimal', placeholder: 'es. 3' })}</label>
            : <label>Importo dell'agenzia (€){testo('agenzia_importo', { inputMode: 'decimal', placeholder: 'es. 5000' })}</label>}
          <label>A che punto
            <select value={modulo.agenzia_stato} onChange={cambia('agenzia_stato')} aria-label="Stato con l'agenzia">
              {Object.entries(STATI_AGENZIA).map(([k, [, n]]) => <option key={k} value={k}>{n}</option>)}
            </select>
          </label>
          <label>Concordata il{data('agenzia_data_concordata')}</label>
          <label>Fatturata il (agenzia){data('agenzia_data_fatturata')}</label>
          <label>Incassata il (agenzia){data('agenzia_data_incassata')}</label>
        </div>
      </fieldset>
      <fieldset>
        <legend>Quota del collaboratore e spese</legend>
        <div className="imp-campi">
          {/* di chi è la quota: di base chi segue il cliente; con più collaboratori si sceglie, e la percentuale
              si rifà da sola su quella di quella persona */}
          {persone.length > 0 && (
            <label>Di chi è la quota
              <select value={modulo.quota_persona} onChange={(e) => setModulo(cambiaPersonaQuota(modulo, persone, e.target.value))}
                aria-label="Di chi è la quota">
                {!persone.some((p) => p.id === modulo.quota_persona) && <option value={modulo.quota_persona}>—</option>}
                {persone.map((p) => <option key={p.id} value={p.id}>{p.nome}</option>)}
              </select>
            </label>
          )}
          <label>Percentuale del collaboratore (%){testo('quota_percentuale', { inputMode: 'decimal', placeholder: 'es. 20' })}</label>
        </div>
        <div className="imp-righe" style={{ marginTop: '.6rem' }}>
          {modulo.spese.map((s, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <div key={i}>
              <input className="imp-input" style={{ flex: 1, minWidth: '12rem', width: 'auto' }} value={s.descrizione}
                onChange={spesa(i, 'descrizione')} placeholder="Spesa (es. visura)" aria-label={`Spesa ${i + 1}`} />
              <input className="imp-input" style={{ width: '8rem' }} value={s.importo} onChange={spesa(i, 'importo')}
                inputMode="decimal" placeholder="€" aria-label={`Importo della spesa ${i + 1}`} />
              <button type="button" className="btn xs rosso-vuoto" onClick={() => setModulo({ ...modulo, spese: modulo.spese.filter((_, j) => j !== i) })}>Togli</button>
            </div>
          ))}
        </div>
        <div className="imp-azioni">
          <button type="button" className="btn sm" onClick={() => setModulo({ ...modulo, spese: [...modulo.spese, { descrizione: '', importo: '' }] })}>+ Spesa</button>
        </div>
      </fieldset>
      <fieldset>
        <legend>La pratica</legend>
        <div className="imp-campi">
          <label>A che punto
            <select value={modulo.stato} onChange={cambia('stato')} aria-label="Stato della provvigione">
              {Object.entries(STATI).map(([k, [, n]]) => <option key={k} value={k}>{n}</option>)}
            </select>
          </label>
          <label>Prevista il{data('data_prevista')}</label>
          <label>Fatturata il{data('data_fatturata')}</label>
          <label>Importo fatturato (€){testo('importo_fatturato', { inputMode: 'decimal' })}</label>
          <label>Incassata il{data('data_incassata')}</label>
          <label>Importo incassato (€){testo('importo_incassato', { inputMode: 'decimal' })}</label>
          <label className="largo">Note (solo per noi)
            <textarea value={modulo.note} onChange={cambia('note')} rows={2} />
          </label>
        </div>
      </fieldset>
      {nuova && (
        <p className="imp-nota" data-testid="modulo-valori-di-base">
          Le caselle partono dai valori di Impostazioni → Provvigioni: cambiate quello che serve.
        </p>
      )}
      <div className="imp-azioni">
        <button type="submit" className="btn sm p" disabled={inVolo}>{inVolo ? 'Salvo…' : 'Salva la provvigione'}</button>
        <button type="button" className="btn sm" onClick={annulla}>Annulla</button>
      </div>
    </form>
  );
};

const Provvigione = ({ propostaId, apribile = false, onCambiata }) => {
  const [dati, setDati] = useState(null);
  const [problema, setProblema] = useState(null);
  const [modulo, setModulo] = useState(null);
  const [inVolo, setInVolo] = useState(false);
  const [aperta, setAperta] = useState(!apribile);

  const carica = useCallback(() => {
    setProblema(null);
    return axios.get(`${API}${ROTTE.pratica(propostaId)}`)
      .then((r) => {
        if (!sembraPratica(r.data)) { setDati(false); setProblema(GENERICI.vuoto); return; }
        setDati(r.data);
      })
      .catch((e) => { setDati(false); setProblema(perche(e)); });
  }, [propostaId]);

  useEffect(() => { if (aperta) carica(); }, [aperta, carica]);

  if (apribile && !aperta) {
    return (
      <button type="button" className="btn sm" onClick={() => setAperta(true)} data-testid={`apri-provvigione-${propostaId}`}>
        Provvigione
      </button>
    );
  }

  const dopoIlSalvataggio = (r) => {
    if (sembraPratica(r?.data)) setDati(r.data); else carica();
    if (onCambiata) onCambiata();
  };

  // Si salva il modulo (PUT) e, se «A che punto» è cambiato, il passo di stato va dalla sua rotta (POST …/stato):
  // è quella che fa i passi uno alla volta e scrive la data. Prima di partire il modulo controlla da sé, così non
  // si salva metà pratica per poi sentirsi dire di no.
  const salva = async () => {
    const statoOra = statoDi(dati);
    const statoAgenziaOra = statoAgenziaDi(dati);
    const problema = controllaStati(modulo, statoOra, statoAgenziaOra);
    if (problema) { toast.error(problema); return; }
    setInVolo(true);
    try {
      let r = await axios.put(`${API}${ROTTE.pratica(propostaId)}`, corpoDaModulo(modulo));
      if (modulo.stato !== statoOra) {
        r = await axios.post(`${API}${ROTTE.stato(propostaId)}`, {
          quale: 'pratica', stato: modulo.stato, data: modulo[CASELLA_DATA[modulo.stato]] || undefined,
          importo: modulo[modulo.stato === 'incassata' ? 'importo_incassato' : 'importo_fatturato'] || undefined,
        });
      }
      if (modulo.agenzia_stato !== statoAgenziaOra) {
        r = await axios.post(`${API}${ROTTE.stato(propostaId)}`, {
          quale: 'agenzia', stato: modulo.agenzia_stato, data: modulo[CASELLA_DATA_AGENZIA[modulo.agenzia_stato]] || undefined,
        });
      }
      toast.success('Provvigione salvata');
      setModulo(null);
      dopoIlSalvataggio(r);
    } catch (e) {
      toast.error(perche(e, { ...GENERICI, 404: 'Non salvata: le provvigioni non sono ancora collegate al server.', errore: 'Provvigione non salvata' }));
    } finally { setInVolo(false); }
  };

  // Un passo avanti (prevista → fatturata → incassata, o con l'agenzia): la data e l'importo li mette il server
  const avanza = async (quale, stato) => {
    setInVolo(true);
    try {
      const r = await axios.post(`${API}${ROTTE.stato(propostaId)}`, { quale, stato });
      toast.success(quale === 'agenzia' ? `Con l'agenzia: ${stato}` : `Provvigione ${stato}`);
      dopoIlSalvataggio(r);
    } catch (e) {
      toast.error(perche(e, { ...GENERICI, errore: 'Stato non cambiato' }));
    } finally { setInVolo(false); }
  };

  const testa = (extra) => (
    <div className="ttl"><div className="i"><Icona nome="euro" /></div><h2>Provvigione</h2>{extra}</div>
  );

  if (dati === null) {
    return <div className="prov" data-testid={`provvigione-${propostaId}`}>{testa()}<p className="vuoto">Carico la provvigione…</p></div>;
  }

  // Niente provvigione: la rotta non c'è, la pratica non è nostra, o non è ancora stata compilata
  if (dati === false || dati.c_e === false) {
    const puoCompilare = dati !== false && Boolean(dati.conti !== undefined || dati.provvigione !== undefined);
    return (
      <div className="prov" data-testid={`provvigione-${propostaId}`}>
        {testa()}
        <p className="vuoto" data-testid="provvigione-problema">
          {dati === false ? problema : 'Questa pratica non ha ancora una provvigione: compilatela quando sapete compenso e percentuali.'}
        </p>
        {puoCompilare && !modulo && (
          <div className="imp-azioni">
            <button type="button" className="btn sm p" onClick={() => setModulo(moduloDa(dati))}>Compila la provvigione</button>
          </div>
        )}
        {modulo && <Modulo modulo={modulo} setModulo={setModulo} salva={salva} annulla={() => setModulo(null)} inVolo={inVolo}
          persone={dati.persone || []} nuova />}
      </div>
    );
  }

  const p = dati.provvigione || {};
  const c = dati.conti;                  // i conti: solo all'admin
  const q = dati.quota;                  // la sola quota: al collaboratore
  const date = dati.date || {};
  const stato = tagStato(p.stato || q?.stato);
  const statoAgenzia = p.agenzia?.stato;
  const puoModificare = Boolean(c || dati.provvigione);   // solo l'admin riceve i conti, e solo lui può scrivere

  return (
    <div className="prov" data-testid={`provvigione-${propostaId}`}>
      {testa(<>
        <span className={`tag ${stato[0]}`} data-testid="stato-provvigione">{stato[1]}</span>
        {puoModificare && !modulo && <button type="button" className="link" onClick={() => setModulo(moduloDa(dati))}>Modifica</button>}
      </>)}

      {!modulo && (
        <>
          <div className="kv">
            {c && <Voce nome="Compenso dal cliente" valore={soldi(c.compenso_cliente_totale)} testid="voce-compenso" />}
            {c && <Voce nome={`Imponibile${p.compenso?.tipo === 'percentuale' ? ` · ${percento(p.compenso.percentuale)} di ${soldi(c.prezzo)}` : ''}`} valore={soldi(c.compenso_cliente_imponibile)} />}
            {c && <Voce nome="IVA" valore={p.compenso?.iva ? soldi(c.compenso_cliente_iva) : 'senza IVA'} />}
            {c && <Voce nome={`Dall'agenzia${dati.agenzia_nome ? ` · ${dati.agenzia_nome}` : ''}`} valore={soldi(c.provvigione_agenzia)} testid="voce-agenzia" />}
            {statoAgenzia && <Voce nome="Con l'agenzia" valore={[tagStato(statoAgenzia, STATI_AGENZIA)[1],
              giorno(p.agenzia?.data_incassata || p.agenzia?.data_fatturata || p.agenzia?.data_concordata)].filter(Boolean).join(' · ')} />}
            {c && <Voce nome="Incasso lordo" valore={soldi(c.incasso_lordo)} testid="voce-lordo" />}
            {c && <Voce nome="Spese" valore={soldi(c.spese_pratica)} />}
            {c && <Voce nome="Incasso netto" valore={soldi(c.incasso_netto)} classe="g" testid="voce-netto" />}
            {c && <Voce nome={['Quota del collaboratore', nomePersona(dati.persone, p.quota?.persona_id),
              c.quota_percentuale !== null && c.quota_percentuale !== undefined ? percento(c.quota_percentuale) : null].filter(Boolean).join(' · ')}
              valore={soldi(c.quota_collaboratore)} testid="voce-quota" />}
            {c && <Voce nome="Resta a MK" valore={soldi(c.margine_mk)} testid="voce-margine" />}
            {q && <Voce nome={`La vostra quota${q.percentuale !== null && q.percentuale !== undefined ? ` · ${percento(q.percentuale)}` : ''}`}
              valore={soldi(q.importo)} classe="g" testid="voce-quota" />}
            {q && <Voce nome="Quando" valore={giorno(q.data)} />}
            <Voce nome="Prevista il" valore={giorno(date.prevista)} />
            {date.fatturata && <Voce nome="Fatturata il" valore={[giorno(date.fatturata), soldi(c?.importo_fatturato)].filter(Boolean).join(' · ')} />}
            {date.incassata && <Voce nome="Incassata il" valore={[giorno(date.incassata), soldi(c?.importo_incassato)].filter(Boolean).join(' · ')} />}
          </div>
          {(p.spese || []).length > 0 && (
            <p className="imp-nota" data-testid="spese-provvigione">Spese: {p.spese.map((s) => `${s.descrizione} ${soldi(s.importo)}`).join(' · ')}</p>
          )}
          {p.note && <p className="prov-note">{p.note}</p>}
          {q && <p className="imp-nota" data-testid="solo-quota">Di questa pratica vedete la vostra quota: il resto lo vede chi si occupa dei conti.</p>}
          {puoModificare && (
            <div className="imp-azioni">
              {DOPO[p.stato] && <button type="button" className="btn sm p" disabled={inVolo} onClick={() => avanza('pratica', DOPO[p.stato])}>Segna {DOPO[p.stato]}</button>}
              {statoAgenzia && DOPO_AGENZIA[statoAgenzia] && (
                <button type="button" className="btn sm" disabled={inVolo} onClick={() => avanza('agenzia', DOPO_AGENZIA[statoAgenzia])}>
                  Agenzia: segna {DOPO_AGENZIA[statoAgenzia]}
                </button>
              )}
            </div>
          )}
        </>
      )}

      {modulo && <Modulo modulo={modulo} setModulo={setModulo} salva={salva} annulla={() => setModulo(null)} inVolo={inVolo}
        persone={dati.persone || []} />}
    </div>
  );
};

export default Provvigione;
